import { useState, useEffect } from 'react'
import { Wifi, WifiOff, Clock, LogOut, Settings } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from '../ui/Button'
import toast from 'react-hot-toast'
// import { cn } from '../../lib/utils'

const Header: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date())
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const { signOut, user } = useAuth()
  const navigate = useNavigate()

  const handleLogout = async () => {
    try {
      await signOut()
      toast.success('Αποσυνδέθηκε επιτυχώς')
      navigate('/login')
    } catch (error) {
      toast.error('Σφάλμα κατά την αποσύνδεση')
      console.error('Logout error:', error)
    }
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)

    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      clearInterval(timer)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('el-GR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })
  }

  const formatDate = (date: Date, includeYear: boolean = true) => {
    return date.toLocaleDateString('el-GR', {
      weekday: 'long',
      ...(includeYear && { year: 'numeric' }),
      month: 'long',
      day: 'numeric'
    })
  }

  return (
    <header className="bg-background shadow-sm border-b border-border px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Current Date and Time */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Clock className="w-5 h-5 text-muted-foreground" />
            <div>
              <div className="text-lg font-semibold text-foreground">
                {formatTime(currentTime)}
              </div>
              <div className="text-sm text-muted-foreground">
                <span className="hidden md:inline">{formatDate(currentTime, true)}</span>
                <span className="md:hidden">{formatDate(currentTime, false)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Status Indicators */}
        <div className="flex items-center space-x-4">
          {/* Connection Status */}
          <div className="flex items-center space-x-2">
            {isOnline ? (
              <>
                <Wifi className="w-5 h-5 text-green-500" />
                <span className="hidden md:inline text-sm text-green-600 font-medium">Συνδεδεμένο</span>
              </>
            ) : (
              <>
                <WifiOff className="w-5 h-5 text-red-500" />
                <span className="hidden md:inline text-sm text-red-600 font-medium">Εκτός σύνδεσης</span>
              </>
            )}
          </div>

          {/* User Email, Settings & Logout */}
          {user && (
            <div className="flex items-center space-x-2 border-l border-border pl-4">
              <span className="text-sm text-muted-foreground hidden sm:inline">
                {user.email}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/settings')}
                className="gap-2 md:hidden"
                title="Ρυθμίσεις"
              >
                <Settings className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="gap-2"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Αποσύνδεση</span>
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

export default Header 